<template>
  <div
    class="folders-provider"
    :class="{
      'is-sidebar-minimized': isSidebarMinimized,
      'single-essay-page': isSingleEssayPage,
    }"
  >
    <slot :loading-folders="loadingFolders" />
  </div>
</template>

<script>
import Vue from "vue";
import { getFolders, getFolder } from "@/api";
import { PLATFORMS } from "@/constants";
import { onboardingContext } from "@/init/onboarding/onboardingContext";

const processFolders = (folders) => {
  return folders.map((folder) => {
    const processFolder = (folder) => {
      const processedFolder = { ...folder };

      // Process children if they exist
      if (processedFolder.children && processedFolder.children.length > 0) {
        processedFolder.children = processedFolder.children.map((child) =>
          processFolder(child)
        );
      }

      // Apply our specific rules using hierarchyLevel:
      // 1. Level 3 folders always get the properties
      // 2. Level 2 folders with no children get the properties
      if (
        processedFolder.hierarchyLevel === 3 ||
        (processedFolder.hierarchyLevel === 2 && processedFolder.maxDepth === 2)
      ) {
        processedFolder.isAssignment = true;
      }

      return processedFolder;
    };

    return processFolder(folder);
  });
};

export default {
  name: "WorkspacesProvider",

  provide() {
    return {
      workspacesProvider: this,
      updateSidebarState: this.updateSidebarState,
    };
  },

  data() {
    return {
      workspaces: {},
      loadingFolders: true,
      loadingFolder: false,
      isSidebarMinimized: false,
      folderAncestorsCache: new Map(),
    };
  },

  computed: {
    isSingleEssayPage() {
      return this.$route.name === "workspace-essay-view";
    },
  },

  async created() {
    if (!this.$route.meta.requiresAuth) {
      const unwatch = this.$watch(
        () => this.$route.meta.requiresAuth,
        (requiresAuth) => {
          if (requiresAuth) {
            this.init();
            unwatch();
          }
        }
      );
    } else {
      this.init();
    }
  },

  methods: {
    updateSidebarState(isMinimized) {
      this.isSidebarMinimized = isMinimized;
    },

    async init() {
      this.loadingFolders = true;
      const data = await getFolders();
      Object.keys(data).forEach((workspaceKey) => {
        Vue.set(this.workspaces, workspaceKey, {
          folders: [],
          foldersMap: {},
        });
        const folders = processFolders(data[workspaceKey]);
        this.setFolders(workspaceKey, folders);
      });
      this.loadingFolders = false;
    },

    async loadFolderById(workspaceKey, id) {
      this.loadingFolder = true;
      const folderData = await getFolder(id);

      const folder = this.getFolderById(workspaceKey, folderData.id);

      if (folderData.gradingDetails) {
        Vue.set(folder, "gradingDetails", folderData.gradingDetails);
        Vue.set(folder, "essaysCount", folderData.essaysCount || 0);
      }

      if (folderData.children) {
        folderData.children.forEach((child) => {
          const existingChild = folder.children.find((c) => c.id === child.id);

          Vue.set(existingChild, "essaysCount", child.essaysCount);
        });
      }

      this.loadingFolder = false;
    },

    _updateOnboardingHasClasses() {
      onboardingContext.hasClasses = this.getAllFolders().length > 0;
    },

    setFolders(workspaceKey, folders) {
      this.resetFolderAncestorsCache(workspaceKey);
      this.workspaces[workspaceKey].folders = folders;
      this.workspaces[workspaceKey].foldersMap = {};
      this.buildMap(workspaceKey, folders);
      this._updateOnboardingHasClasses();
    },

    getFolderAncestors(workspaceKey, id) {
      const cacheKey = `${workspaceKey}-${id}`;
      if (this.folderAncestorsCache.has(cacheKey)) {
        return this.folderAncestorsCache.get(cacheKey);
      }

      let node = this.getFolderById(workspaceKey, id);
      const ancestors = [node];

      while (node?.parentId) {
        node = this.getFolderById(workspaceKey, node.parentId);
        ancestors.push(node);
      }

      const result = ancestors.reverse();
      this.folderAncestorsCache.set(cacheKey, result);
      return result;
    },

    buildMap(workspaceKey, nodes) {
      for (const node of nodes) {
        Vue.set(this.workspaces[workspaceKey].foldersMap, node.id, node);

        if (node.children?.length) {
          this.buildMap(workspaceKey, node.children);
        }
      }
    },

    getAllSiblingsById(workspaceKey, id) {
      const currentFolder = this.getFolderById(workspaceKey, id);
      return this.findAllFolders(
        workspaceKey,
        (folder) => folder.parentId === currentFolder.parentId
      );
    },

    getNextSiblingById(workspaceKey, id) {
      const siblings = this.getAllSiblingsById(workspaceKey, id);
      const index = siblings.findIndex((folder) => folder.id === id);
      return index >= 0 && index < siblings.length - 1
        ? siblings[index + 1]
        : null;
    },

    getPreviousSiblingById(workspaceKey, id) {
      const siblings = this.getAllSiblingsById(workspaceKey, id);
      const index = siblings.findIndex((folder) => folder.id === id);
      return index > 0 ? siblings[index - 1] : null;
    },

    getClosestSiblingById(workspaceKey, id) {
      return (
        this.getNextSiblingById(workspaceKey, id) ??
        this.getPreviousSiblingById(workspaceKey, id) ??
        null
      );
    },

    getFolderById(workspaceKey, id) {
      return this.workspaces?.[workspaceKey]?.foldersMap?.[id] || null;
    },

    updateFolderById(workspaceKey, id, newData) {
      this.resetFolderAncestorsCache(workspaceKey, id);
      const node = this.getFolderById(workspaceKey, id);

      if (!node) {
        console.error("Folder not found", workspaceKey, id);

        return;
      }

      Object.keys(newData).forEach((key) => {
        Vue.set(node, key, newData[key]);
      });
    },

    removeFolderById(workspaceKey, id) {
      this.resetFolderAncestorsCache(workspaceKey, id);
      const node = this.getFolderById(workspaceKey, id);

      if (!node) return;

      // NOTE: remove from parent
      if (node.parentId !== null) {
        const parent = this.getFolderById(workspaceKey, node.parentId);

        if (parent?.children) {
          const idx = parent.children.findIndex((c) => c.id === id);

          if (idx !== -1) parent.children.splice(idx, 1);
        }
      } else {
        const idx = this.workspaces[workspaceKey].folders.findIndex(
          (c) => c.id === id
        );

        if (idx !== -1) this.workspaces[workspaceKey].folders.splice(idx, 1);
      }

      // NOTE: remove from Map
      const deleteRecursive = (node) => {
        delete this.workspaces[workspaceKey].foldersMap[node.id];

        if (node.children) {
          for (const child of node.children) deleteRecursive(child);
        }
      };
      deleteRecursive(node);
      this._updateOnboardingHasClasses();
    },

    // NOTE: extended helpers
    /**
     * Find node by arbitrary condition
     * @param {Function} predicate (node) => boolean
     * @returns {Object|null}
     */
    findFolder(workspaceKey, predicate) {
      for (const node of Object.values(
        this.workspaces[workspaceKey]?.foldersMap || {}
      )) {
        if (predicate(node)) return node;
      }

      return null;
    },

    /**
     * Find all nodes by condition
     */
    findAllFolders(workspaceKey, predicate = () => true) {
      return Object.values(
        this.workspaces?.[workspaceKey]?.foldersMap || {}
      ).filter(predicate);
    },

    /**
     * Get all existing folders in a plain array
     */
    getAllFolders() {
      return [
        PLATFORMS.ESSAYGRADER,
        PLATFORMS.CANVAS,
        PLATFORMS.GOOGLE,
        PLATFORMS.SCHOOLOGY,
      ]
        .map((platform) => this.workspaces?.[platform]?.folders || [])
        .flat();
    },

    /**
     * Add folder to parent, found by condition
     * If no parent is found and folder.parentId is null, adds to root
     */
    addFolderWhere(workspaceKey, predicate, folders) {
      this.resetFolderAncestorsCache(workspaceKey);

      const parent = this.findFolder(workspaceKey, predicate);

      folders = Array.isArray(folders)
        ? processFolders(folders)
        : processFolders([folders]);

      if (!parent) {
        // If no parent found and folder should be added to root
        if (
          folders.every(
            (folder) =>
              folder.parentId === null || folder.parentId === undefined
          )
        ) {
          if (!this.workspaces[workspaceKey]) {
            Vue.set(this.workspaces, workspaceKey, {
              folders: [],
              foldersMap: {},
            });
          }

          this.workspaces[workspaceKey].folders.push(...folders);
          this.buildMap(workspaceKey, folders);
          this._updateOnboardingHasClasses();

          return true;
        }

        return false;
      }

      if (!parent.children) Vue.set(parent, "children", []);

      parent.children.push(...folders);
      this.buildMap(workspaceKey, folders);
      this._updateOnboardingHasClasses();

      return true;
    },

    /**
     * Update folders by condition
     */
    updateFoldersWhere(workspaceKey, predicate, newData) {
      this.resetFolderAncestorsCache(workspaceKey);

      this.findAllFolders(workspaceKey, predicate).forEach((node) => {
        this.updateFolderById(workspaceKey, node.id, newData);
      });
    },

    /**
     * Find workspace key by folder id
     * @param {string} folderId
     * @returns {string|null}
     */
    findWorkspaceKeyByFolderId(folderId) {
      for (const workspaceKey of Object.keys(this.workspaces)) {
        const folder = this.findFolder(
          workspaceKey,
          (folder) => folder.id === folderId
        );

        if (folder) return workspaceKey;
      }

      return null;
    },

    extractClassesAndAssignments(
      folders = this.workspaces[PLATFORMS.ESSAYGRADER]?.folders || [],
      classes = [],
      assignments = []
    ) {
      folders.forEach((folder) => {
        if (
          (folder.maxDepth === 3 && folder.hierarchyLevel === 2) ||
          (folder.maxDepth === 2 && folder.hierarchyLevel === 1)
        ) {
          classes.push(folder);
        }

        if (
          (folder.maxDepth === 3 && folder.hierarchyLevel === 3) ||
          (folder.maxDepth === 2 && folder.hierarchyLevel === 2)
        ) {
          assignments.push(folder);
        }

        if (folder.children?.length) {
          this.extractClassesAndAssignments(
            folder.children,
            classes,
            assignments
          );
        }
      });

      return { classes, assignments };
    },

    resetFolderAncestorsCache(workspaceKey, id) {
      if (!workspaceKey && !id) {
        this.folderAncestorsCache.clear();
        return;
      }

      if (workspaceKey && id) {
        const cacheKey = `${workspaceKey}-${id}`;
        this.folderAncestorsCache.delete(cacheKey);
        return;
      }

      if (workspaceKey && !id) {
        for (const key of this.folderAncestorsCache.keys()) {
          if (key.startsWith(`${workspaceKey}-`)) {
            this.folderAncestorsCache.delete(key);
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.folders-provider {
  display: contents;
}
</style>
